'use client';

/**
 * LeanDashboard — bespoke body for the "Lean" persona.
 *
 * Lean users want a fast, low-noise read of whether the cut is working:
 * deficit landing, weight dropping at the right rate, protein floor held.
 * The killer metric is the Cut Tracker — it joins those three signals
 * into a single ON PACE / TOO FAST / TOO SLOW verdict.
 *
 * Layout:
 *   1. Start Workout (dominant CTA)
 *   2. Today's fuel (calories / protein / steps)
 *   3. Weight trend — compact strip (current + 7d/30d delta)
 *   4. Cut Tracker — deficit × pace × protein synthesis (the killer card)
 *   5. A single recovery line (collapsed from the full recovery cluster)
 *   6. Week-at-a-glance schedule
 *
 * Reuses the shared StartWorkoutCard + DailyStatusRow + WeightStrip +
 * CutSynthesisCard so the start-workout, macro, weight, and cut-tracker
 * logic stay single-sourced.
 */

import { displayReadiness } from '@/lib/unifiedIntelligence';
import StartWorkoutCard from '@/components/dashboard/StartWorkoutCard';
import DailyStatusRow from '@/components/dashboard/DailyStatusRow';
import WeekScheduleCard from '@/components/dashboard/WeekScheduleCard';
import WeightStrip from '@/components/dashboard/WeightStrip';
import CutSynthesisCard from '@/components/dashboard/CutSynthesisCard';
import type { DashboardData } from '@/lib/hooks/useDashboardData';

function readinessLine(readiness: number | null | undefined): { label: string; tw: string; dot: string } {
  if (readiness == null || readiness <= 0) {
    return { label: 'No recovery signal yet — log a check-in', tw: 'text-slate-400', dot: 'bg-slate-600' };
  }
  if (readiness >= 70) return { label: `Recovered — good to push (${Math.round(readiness)})`, tw: 'text-emerald-400', dot: 'bg-emerald-400' };
  if (readiness >= 40) return { label: `Moderate — train, don't overreach (${Math.round(readiness)})`, tw: 'text-amber-400', dot: 'bg-amber-400' };
  return { label: `Low — keep it light or rest (${Math.round(readiness)})`, tw: 'text-red-400', dot: 'bg-red-400' };
}

export default function LeanDashboard({ data }: { data: DashboardData }) {
  const { user, todayActivity, dailyCalories, activeBreak } = data;
  // displayReadiness, not the raw field: returns null when the score is the
  // provisional 70 constant, which routes into the honest "no signal yet" copy.
  const readiness = displayReadiness(data.intelligenceReport);
  const r = readinessLine(readiness);

  return (
    <div className="space-y-3">
      {/* 1. Start Workout — the one thing Lean opens the app to do.
          (Hidden during an active travel/injury break, same as Full.) */}
      {!activeBreak && <StartWorkoutCard data={data} />}

      {/* 2. Today's fuel — calories / protein / steps at a glance. */}
      <DailyStatusRow
        userId={user?.uid}
        todayActivity={todayActivity}
        dailyCalories={dailyCalories}
      />

      {/* 3. Weight trend — compact strip from the smart scale. */}
      <WeightStrip userId={user?.uid} />

      {/* 4. Cut tracker — deficit × pace × protein. The killer Lean card. */}
      <CutSynthesisCard userId={user?.uid} />

      {/* 5. Recovery — collapsed to a single status line. Lean keeps the
          signal but drops the Z2 / cardiac-age / personal-state stack. */}
      <button
        onClick={() => { window.location.href = '/recovery'; }}
        className="w-full flex items-center gap-2.5 rounded-lg border border-operator-steel/40 bg-operator-slate/50 px-3.5 py-2.5 text-left hover:border-operator-steel transition"
      >
        <span className={`w-2 h-2 rounded-full ${r.dot} shrink-0`} aria-hidden />
        <span className={`text-sm font-medium ${r.tw} flex-1`}>{r.label}</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600">Recovery →</span>
      </button>

      {/* 6. Week-at-a-glance — non-gamified schedule of what's coming. */}
      {data.programSelection && (
        <div className="rounded-xl border border-operator-steel/40 bg-operator-slate/40 p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-2">This Week</div>
          <WeekScheduleCard
            currentWeek={data.currentWeek}
            currentDay={data.currentDay}
            programSelection={data.programSelection}
          />
        </div>
      )}
    </div>
  );
}
